"use client";

import { useCallback, useMemo } from "react";
import { authClient } from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { organizationsApi } from "@/lib/api";
import type { OrgRecord, MemberRecord } from "@/lib/api";

export type { OrgRecord, MemberRecord };

export function useOrganization() {
  const { activeOrganizationId, setActiveOrganization } = useAuth();

  const activeOrg = authClient.useActiveOrganization();
  const listOrgs = authClient.useListOrganizations();

  const activeOrganization = activeOrg.data ?? null;
  const organizations = listOrgs.data ?? [];
  const isLoading = activeOrg.isPending || listOrgs.isPending;

  const isAdmin = useMemo(() => {
    if (!activeOrganization) return false;
    const members = (activeOrganization as Record<string, unknown>).members as
      | Array<{ userId: string; role: string }>
      | undefined;
    // Check via activeMember role on the org data
    const role = (activeOrganization as Record<string, unknown>).activeMemberRole as
      | string
      | undefined;
    if (role === "owner" || role === "admin") return true;
    // Fallback: check members array
    if (members) {
      return members.some((m) => m.role === "owner" || m.role === "admin");
    }
    return false;
  }, [activeOrganization]);

  // SFWL-003: View-only members can read but cannot mutate. Source of truth
  // is the WriteAccessGuard on the API; this flag is UX-only (hide buttons).
  const isViewOnly = useMemo(() => {
    if (!activeOrganization) return false;
    const org = activeOrganization as Record<string, unknown>;
    const activeMember = org.activeMember as { viewOnly?: boolean } | undefined;
    if (activeMember?.viewOnly === true) return true;
    const activeMemberUserId = org.activeMemberUserId as string | undefined;
    const members = org.members as
      | Array<{ userId: string; viewOnly?: boolean }>
      | undefined;
    if (members && activeMemberUserId) {
      const me = members.find((m) => m.userId === activeMemberUserId);
      if (me?.viewOnly === true) return true;
    }
    return false;
  }, [activeOrganization]);

  const inviteMember = useCallback(
    async (email: string, role: "admin" | "member") => {
      if (!activeOrganizationId) throw new Error("No active organization");
      const result = await authClient.organization.inviteMember({
        organizationId: activeOrganizationId,
        email,
        role,
      });
      if (result.error) {
        throw new Error(result.error.message || "Failed to invite member");
      }
      return result.data;
    },
    [activeOrganizationId]
  );

  const updateMemberRole = useCallback(
    async (memberId: string, role: "admin" | "member") => {
      if (!activeOrganizationId) throw new Error("No active organization");
      const result = await authClient.organization.updateMemberRole({
        organizationId: activeOrganizationId,
        memberId,
        role,
      });
      if (result.error) {
        throw new Error(result.error.message || "Failed to update role");
      }
      return result.data;
    },
    [activeOrganizationId]
  );

  const removeMember = useCallback(
    async (memberIdOrEmail: string) => {
      if (!activeOrganizationId) throw new Error("No active organization");
      const result = await authClient.organization.removeMember({
        organizationId: activeOrganizationId,
        memberIdOrEmail,
      });
      if (result.error) {
        throw new Error(result.error.message || "Failed to remove member");
      }
      return result.data;
    },
    [activeOrganizationId]
  );

  const cancelInvitation = useCallback(async (invitationId: string) => {
    const result = await authClient.organization.cancelInvitation({
      invitationId,
    });
    if (result.error) {
      throw new Error(result.error.message || "Failed to cancel invitation");
    }
    return result.data;
  }, []);

  const getChildren = useCallback(
    async (orgId?: string) => {
      const id = orgId || activeOrganizationId;
      if (!id) return [];
      return organizationsApi.getChildren(id);
    },
    [activeOrganizationId]
  );

  const getTree = useCallback(
    async (orgId?: string) => {
      const id = orgId || activeOrganizationId;
      if (!id) return [];
      return organizationsApi.getTree(id);
    },
    [activeOrganizationId]
  );

  const createChild = useCallback(
    async (data: { name: string; slug?: string; logo?: string }) => {
      if (!activeOrganizationId) throw new Error("No active organization");
      return organizationsApi.createChild(activeOrganizationId, data);
    },
    [activeOrganizationId]
  );

  return {
    activeOrganization,
    activeOrganizationId,
    organizations,
    isLoading,
    isAdmin,
    isViewOnly,
    setActiveOrganization,
    inviteMember,
    updateMemberRole,
    removeMember,
    cancelInvitation,
    getChildren,
    getTree,
    createChild,
  };
}
